import React, { useEffect, useState } from "react";
import VideogameAssetOutlinedIcon from "@mui/icons-material/VideogameAssetOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import CasinoOutlinedIcon from "@mui/icons-material/CasinoOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { winsApi } from "../services/api";
import { formatPlayTable, formatTRYWhole } from "../utils/money";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { readPrefs } from "../utils/prefs";
import { ORIGINAL_COVERS } from "../utils/originalCovers";

const ORIGINALS = [
  { test: /aviator/, title: "Aviator", cover: ORIGINAL_COVERS.aviator },
  { test: /plinko/, title: "Plinko", cover: ORIGINAL_COVERS.plinko },
  { test: /limbo/, title: "Limbo", cover: ORIGINAL_COVERS.limbo },
  { test: /may[iı]n|mines/, title: "Mayın", cover: ORIGINAL_COVERS.mines },
  { test: /çark|cark|wheel/, title: "Çark", cover: ORIGINAL_COVERS.wheel },
  { test: /(^|\s)zar(\s|$)|dice/, title: "Zar", cover: ORIGINAL_COVERS.dice },
  { test: /blackjack/, title: "Blackjack", cover: ORIGINAL_COVERS.blackjack },
];

function presentGame(name, banner) {
  const key = fold(name);
  const orig = ORIGINALS.find((item) => item.test.test(key));
  return {
    title: String(name || orig?.title || "Oyun").trim(),
    cover: banner || orig?.cover || "",
  };
}

const fold = (value = "") => String(value).toLocaleLowerCase("tr-TR");

const gameLook = (name) => {
  const n = fold(name);
  if (/aviator|crash|limbo/.test(n)) return { Icon: FlightTakeoffOutlinedIcon, tone: "crash" };
  if (/rulet|roulette|blackjack|baccarat|poker/.test(n)) return { Icon: CasinoOutlinedIcon, tone: "live" };
  if (/zar|dice|may[iı]n|mines|plinko|cark|wheel/.test(n)) return { Icon: AutoAwesomeOutlinedIcon, tone: "orig" };
  return { Icon: VideogameAssetOutlinedIcon, tone: "slot" };
};

const isOriginalFeed = (name) => {
  const key = fold(name);
  return ORIGINALS.some((item) => item.test.test(key)) || /marsilya|orijinal/i.test(key);
};

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const rowStats = (row) => {
  const orig = isOriginalFeed(row.game);
  const payoutRaw = Number(row.amount) || 0;
  const multiplier = Number(row.multiplier) || Number((1.15 + ((Number(String(row.id).replace(/\D/g, "").slice(-3)) || 40) % 2200) / 100).toFixed(2));
  const payout = orig ? roundMoney(payoutRaw) : Math.round(payoutRaw);
  const bet = orig
    ? roundMoney(Number(row.bet) || Math.abs(payoutRaw) / Math.max(multiplier, 1))
    : Math.round(Number(row.bet) || Math.abs(payout) / Math.max(multiplier, 1));
  const at = row.at ? new Date(row.at) : null;
  const time = at && !Number.isNaN(at.getTime())
    ? at.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
    : "—";
  return { payout, multiplier, bet, time, lost: payout < 0, orig };
};

const Pay = ({ amount, hidden, orig }) => {
  if (hidden) return <span className="lw-pay is-hidden">••••</span>;
  const text = orig ? formatPlayTable(amount) : formatTRYWhole(amount);
  return (
    <span className={`lw-pay${amount < 0 ? " is-loss" : ""}`}>
      {text}
      {orig ? null : <i className="lw-chip" aria-hidden>₺</i>}
    </span>
  );
};

const GameMark = ({ cover, look }) => {
  const Icon = look.Icon;
  if (cover) {
    return (
      <span className="lw-ico is-cover">
        <img src={cover} alt="" />
      </span>
    );
  }
  return <span className={`lw-ico tone-${look.tone}`}><Icon fontSize="small" /></span>;
};

const LiveWins = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const prefs = { ...readPrefs(), ...(user?.prefs || {}) };

  useEffect(() => {
    let active = true;
    const load = () => {
      winsApi
        .list()
        .then((data) => {
          if (active) setItems((data.items || []).slice(0, 12));
        })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 1000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (!items.length) return null;

  const rows = items.slice(0, 12).map((row) => {
    const shown = presentGame(row.game, row.banner);
    const look = gameLook(shown.title);
    const stats = rowStats(row);
    const hidden = /gizlen/i.test(row.user || "")
      || Boolean(prefs.ghost && user?.username && fold(row.user) === fold(user.username));
    return { row, look, stats, hidden, shown, orig: stats.orig };
  });

  return (
    <section className="lw">
      <div className="lw-tabs">
        <span className="lw-tab on">{t("lobby.liveCasinoBets")}</span>
      </div>

      <div className="lw-mobile">
        <div className="lw-m-head">
          <span>{t("lobby.liveGame")}</span>
          <span>{t("lobby.livePayout")}</span>
        </div>
        {rows.map(({ row, look, stats, shown, orig }) => (
          <div className="lw-m-row" key={row.id}>
            <div className="lw-game">
              <GameMark title={shown.title} cover={shown.cover} look={look} />
              <strong>{shown.title}</strong>
            </div>
            <Pay amount={stats.payout} hidden={prefs.hideWins} orig={orig} />
          </div>
        ))}
      </div>

      <div className="lw-desktop">
        <table className="lw-table">
          <thead>
            <tr>
              <th>{t("lobby.liveGame")}</th>
              <th>{t("lobby.liveUser")}</th>
              <th>{t("lobby.liveTime")}</th>
              <th>{t("lobby.liveBet")}</th>
              <th>{t("lobby.liveMult")}</th>
              <th>{t("lobby.livePayout")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, look, stats, hidden, shown, orig }) => (
                <tr key={row.id}>
                  <td>
                    <div className="lw-game">
                      <GameMark title={shown.title} cover={shown.cover} look={look} />
                      <strong>{shown.title}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="lw-user">
                      <span className={`lw-ava${hidden ? " is-hidden" : ""}`}>
                        {!hidden && row.avatar ? (
                          <img src={row.avatar} alt="" referrerPolicy="no-referrer" />
                        ) : hidden ? (
                          <PersonOffOutlinedIcon fontSize="inherit" />
                        ) : (
                          <PersonOutlineOutlinedIcon fontSize="inherit" />
                        )}
                      </span>
                      <span>{hidden ? t("lobby.liveHidden") : row.user}</span>
                    </div>
                  </td>
                  <td className="lw-muted">{stats.time}</td>
                  <td><span className="lw-money">{prefs.hideWins ? "••••" : (orig ? formatPlayTable(stats.bet) : formatTRYWhole(stats.bet))}</span></td>
                  <td className="lw-muted">{prefs.hideWins || stats.lost ? "—" : `${stats.multiplier.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`}</td>
                  <td className="lw-end"><Pay amount={stats.payout} hidden={prefs.hideWins} orig={orig} /></td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default LiveWins;
